import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  TIMER_ALARM_WINDOW_MS,
  TIMER_MAX_SECONDS,
  canControlTimer,
  timerElapsedMs,
  timerFinishesAtMs,
  timerRemainingMs,
  type Huddle,
  type HuddleTimer,
  type HuddleTimerMode,
} from '@avslack/shared';
import { huddleTimerAction, setHuddleTimerAccess } from '../lib/api';
import { useDismiss } from '../lib/dismiss';
import { Icon } from './Icon';

/**
 * KOZOS STOPPER / IDOZITO az olelesben (a fejlecben).
 *
 * Az allapot a huddle pillanatkepenek resze: mindenki ugyanazt latja, a kesve
 * belepo is. A hazigazda inditja es allitja; ki is nyithatja, hogy barki
 * kezelhesse. A STOPPER csak szalad, amig meg nem allitjak (szunet, nullaz);
 * az IDOZITO visszafele szamol, es lejartakor nagy, animalt ora jelenik meg.
 *
 * A kijelzett idot a kliens szamolja a szerver idejehez igazitva (lasd
 * `useServerClock`), nem leptetett szamlalobol - ugyanazert, mint a HuddleTimer:
 * a hatterbe kerult lap idozitoje ritkul, de az ora ebredeskor azonnal helyes.
 */

const QUICK_COUNTDOWNS: { seconds: number; label: string }[] = [
  { seconds: 30, label: '30 mp' },
  { seconds: 60, label: '1 perc' },
  { seconds: 300, label: '5 perc' },
  { seconds: 600, label: '10 perc' },
  { seconds: 900, label: '15 perc' },
];

/** A szerver mostani ideje (ms) - a pillanatkep keszitesi idejehez igazitva. */
function useServerClock(serverNow: string | null): () => number {
  const [offset, setOffset] = useState(() => (serverNow ? Date.parse(serverNow) - Date.now() : 0));
  useEffect(() => {
    if (serverNow) setOffset(Date.parse(serverNow) - Date.now());
  }, [serverNow]);
  return useCallback(() => Date.now() + offset, [offset]);
}

/** Negyed masodpercenkent ujrarajzol - csak amig van mit mutatni. */
function useTick(active: boolean): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setTick((value) => value + 1), 250);
    return () => clearInterval(timer);
  }, [active]);
}

// ─── "Elintezve": a lejart idozito eltunik, ha leokeztuk ──────────────────────

/**
 * A lejart idozitot a "Rendben" UTAN sem mutatjuk tovabb (a fejlec es a csempe
 * sem) - kulonben a 0:00 a vegtelensegig villogna. Csak NALUNK, helyben: a
 * tobbiek a sajat riasztasukat okezzak le maguk. A kulcs a lejarat idopontjat is
 * tartalmazza, igy egy ujrainditott (nullazott) idozito megint megjelenik.
 */
const dismissedAlarms = new Set<string>();
const dismissListeners = new Set<() => void>();

const alarmKey = (timer: HuddleTimer): string => `${timer.id}:${timerFinishesAtMs(timer) ?? ''}`;

function dismissAlarm(timer: HuddleTimer): void {
  dismissedAlarms.add(alarmKey(timer));
  for (const listener of dismissListeners) listener();
}

function useAlarmDismissed(timer: HuddleTimer | null): boolean {
  const key = timer ? alarmKey(timer) : null;
  return useSyncExternalStore(
    (listener) => {
      dismissListeners.add(listener);
      return () => void dismissListeners.delete(listener);
    },
    () => key !== null && dismissedAlarms.has(key),
    () => false,
  );
}
function formatClock(ms: number, round: 'floor' | 'ceil'): string {
  const total = Math.max(0, round === 'ceil' ? Math.ceil(ms / 1000) : Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${minutes}:${ss}`;
}

// ─── Fejlec-vezerlo ───────────────────────────────────────────────────────────

export function StopwatchControl({
  huddle,
  meId,
  inline = false,
}: {
  huddle: Huddle;
  meId: string;
  /**
   * Telefonon a "Tobb" lap belsejeben all: stopper nelkul a valaszto AZONNAL, a
   * lapon belul latszik (nincs lebego ablak, ami a gorgetheto tartalomba csuszna).
   */
  inline?: boolean;
}) {
  const dismissed = useAlarmDismissed(huddle.timer);
  const timer = dismissed ? null : huddle.timer;
  const isHost = huddle.hostId === meId;
  const canControl = canControlTimer(huddle.hostId, huddle.timerOpen, meId);
  const nowMs = useServerClock(timer?.serverNow ?? null);
  useTick(timer !== null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  useDismiss(pickerRef, () => setPickerOpen(false), {
    enabled: pickerOpen,
    ignore: [buttonRef],
    returnFocus: buttonRef,
  });

  const act = (input: Parameters<typeof huddleTimerAction>[1]) =>
    void huddleTimerAction(huddle.id, input).catch(() => undefined);

  // Nincs stopper: csak aki kezelheti, lat egy gombot.
  if (!timer) {
    if (!canControl) return null;
    if (inline) {
      return (
        <TimerPicker
          huddle={huddle}
          isHost={isHost}
          inline
          onStart={(mode, seconds) =>
            act({
              action: 'start',
              mode,
              ...(mode === 'countdown' ? { durationSeconds: seconds } : {}),
            })
          }
        />
      );
    }
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setPickerOpen((open) => !open)}
          aria-pressed={pickerOpen}
          title="Stopper vagy időzítő indítása"
          className="flex items-center gap-1.5 rounded-pill border border-border-soft bg-surface-raised px-3 py-1.5 text-xs font-semibold text-ink-muted transition-colors hover:border-border-strong hover:text-ink"
        >
          <Icon name="ora" />
          <span className="hidden sm:inline @max-[80rem]/fej:hidden!">Stopper</span>
        </button>
        {pickerOpen && (
          <TimerPicker
            ref={pickerRef}
            huddle={huddle}
            isHost={isHost}
            onStart={(mode, seconds) => {
              setPickerOpen(false);
              act({
                action: 'start',
                mode,
                ...(mode === 'countdown' ? { durationSeconds: seconds } : {}),
              });
            }}
          />
        )}
      </div>
    );
  }

  const now = nowMs();
  const remaining = timerRemainingMs(timer, now);
  const finished = remaining !== null && remaining <= 0;
  const display =
    remaining !== null
      ? formatClock(remaining, 'ceil')
      : formatClock(timerElapsedMs(timer, now), 'floor');

  return (
    <div
      className={[
        'flex shrink-0 items-center gap-1 rounded-pill border px-2.5 py-1 text-xs',
        finished
          ? 'border-[var(--danger)] bg-[var(--danger-soft)] text-ink'
          : timer.running
            ? 'border-accent bg-accent-soft text-ink'
            : 'border-border-soft bg-surface-raised text-ink-muted',
      ].join(' ')}
    >
      <span aria-hidden className={finished ? 'text-[var(--danger)]' : 'text-accent'}>
        <Icon name="ora" />
      </span>
      <span className="sr-only">{timer.mode === 'countdown' ? 'Időzítő' : 'Stopper'}: </span>
      <time
        title={timer.mode === 'countdown' ? 'Hátralévő idő' : 'Eltelt idő'}
        className={['tabular-nums font-semibold', finished ? 'animate-pulse' : ''].join(' ')}
      >
        {display}
      </time>

      {canControl && (
        <>
          <ChipButton
            label={timer.running ? 'Szünet' : 'Folytatás'}
            disabled={finished}
            onClick={() => act({ action: timer.running ? 'pause' : 'resume' })}
          >
            <Icon name={timer.running ? 'szunet' : 'folytatas'} />
          </ChipButton>
          <ChipButton label="Nullázás" onClick={() => act({ action: 'reset' })}>
            ⟲
          </ChipButton>
          <ChipButton label="Eltüntetés a fejlécből" onClick={() => act({ action: 'clear' })}>
            ×
          </ChipButton>
        </>
      )}

      {isHost && (
        <button
          type="button"
          aria-pressed={huddle.timerOpen}
          title={
            huddle.timerOpen
              ? 'Most bárki kezelheti a stoppert — kattints a visszazáráshoz'
              : 'Csak te kezeled — kattints, hogy bárki kezelhesse'
          }
          onClick={() =>
            void setHuddleTimerAccess(huddle.id, !huddle.timerOpen).catch(() => undefined)
          }
          className={[
            'grid size-6 place-items-center rounded-full text-xs transition-colors',
            huddle.timerOpen
              ? 'bg-accent text-accent-ink'
              : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
          ].join(' ')}
        >
          <Icon name="tagok" />
        </button>
      )}
    </div>
  );
}

function ChipButton({
  label,
  onClick,
  disabled = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="grid size-6 place-items-center rounded-full text-xs text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function TimerPicker({
  ref,
  huddle,
  isHost,
  onStart,
  inline = false,
}: {
  ref?: React.Ref<HTMLDivElement>;
  huddle: Huddle;
  isHost: boolean;
  onStart: (mode: HuddleTimerMode, seconds: number) => void;
  /** A lapon belul (telefon): nem lebeg, nincs arnyek es kerete. */
  inline?: boolean;
}) {
  const [mode, setMode] = useState<HuddleTimerMode>('stopwatch');
  const [seconds, setSeconds] = useState(300);

  return (
    <div
      ref={ref}
      className={
        inline
          ? 'space-y-3 text-sm'
          : 'absolute right-0 top-full z-40 mt-2 w-72 animate-fade-rise space-y-3 rounded-card border border-border-soft bg-surface-raised p-3 text-sm shadow-(--shadow-modal)'
      }
    >
      <div className="flex gap-1.5">
        {(['stopwatch', 'countdown'] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={mode === value}
            onClick={() => setMode(value)}
            className={[
              'flex-1 rounded-control border px-3 py-1.5 text-xs font-semibold',
              mode === value
                ? 'border-accent bg-accent-soft text-accent'
                : 'border-border-soft text-ink-muted hover:border-border-strong',
            ].join(' ')}
          >
            {value === 'stopwatch' ? 'Stopper' : 'Időzítő'}
          </button>
        ))}
      </div>

      {mode === 'stopwatch' ? (
        <p className="text-2xs text-ink-faint">
          Addig szalad, amíg meg nem állítjátok (szünet, nullázás).
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {QUICK_COUNTDOWNS.map((quick) => (
              <button
                key={quick.seconds}
                type="button"
                aria-pressed={seconds === quick.seconds}
                onClick={() => setSeconds(quick.seconds)}
                className={[
                  'rounded-pill border px-2.5 py-1 text-2xs font-semibold',
                  seconds === quick.seconds
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border-soft text-ink-muted hover:border-border-strong',
                ].join(' ')}
              >
                {quick.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-2xs text-ink-muted">
            Más:
            <input
              type="number"
              min={1}
              max={TIMER_MAX_SECONDS / 60}
              value={Math.max(1, Math.round(seconds / 60))}
              onChange={(event) =>
                setSeconds(
                  Math.min(TIMER_MAX_SECONDS, Math.max(1, Number(event.target.value) || 1) * 60),
                )
              }
              className="w-16 rounded-control border border-border bg-surface px-2 py-1 text-xs"
            />
            perc
          </label>
          <p className="text-2xs text-ink-faint">
            Visszafelé számol, lejártakor mindenkinél nagy óra jelenik meg.
          </p>
        </div>
      )}

      {isHost && (
        <label className="flex items-center gap-2 text-2xs text-ink-muted">
          <input
            type="checkbox"
            checked={huddle.timerOpen}
            onChange={(event) =>
              void setHuddleTimerAccess(huddle.id, event.target.checked).catch(() => undefined)
            }
          />
          Bárki kezelheti (nem csak a házigazda)
        </label>
      )}

      <button
        type="button"
        onClick={() => onStart(mode, seconds)}
        className="w-full rounded-control bg-accent px-3 py-2 text-xs font-semibold text-accent-ink hover:bg-accent-hover"
      >
        {mode === 'stopwatch'
          ? 'Stopper indítása'
          : `Időzítő indítása (${formatClock(seconds * 1000, 'ceil')})`}
      </button>
    </div>
  );
}

// ─── Lejarat: nagy, animalt ora ───────────────────────────────────────────────

/** Rovid csengo: harom sipszo. Ha a bongeszo nem engedi, halkan kimarad. */
function chime(): void {
  try {
    const ctx = new AudioContext();
    const start = ctx.currentTime;
    for (let i = 0; i < 3; i += 1) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, start + i * 0.35);
      gain.gain.exponentialRampToValueAtTime(0.25, start + i * 0.35 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + i * 0.35 + 0.28);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start + i * 0.35);
      osc.stop(start + i * 0.35 + 0.3);
    }
    window.setTimeout(() => void ctx.close().catch(() => undefined), 1500);
  } catch {
    // A hang kenyelem, nem funkcio.
  }
}

/**
 * A lejart idozito riasztasa - MINDENKINEK, nagyban.
 *
 * Csak a friss lejaratot mutatja (egy percen belul): a kesve belepo ne lasson regi
 * riasztast. Az "elintezve" jelet a stopper azonositojahoz kotjuk, helyben.
 */
export function TimerAlarm({ huddle }: { huddle: Huddle }) {
  const timer = huddle.timer;
  const nowMs = useServerClock(timer?.serverNow ?? null);
  useTick(timer !== null);
  const dismissed = useAlarmDismissed(timer);

  const finishesAt = timer ? timerFinishesAtMs(timer) : null;
  const now = nowMs();
  const ringing =
    timer !== null &&
    finishesAt !== null &&
    now >= finishesAt &&
    now - finishesAt < TIMER_ALARM_WINDOW_MS &&
    !dismissed;

  const timerId = timer?.id ?? null;
  useEffect(() => {
    if (ringing) chime();
    // Csak a megjelenesekor szol, nem minden ujrarajzolaskor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringing && timerId]);

  if (!ringing || !timer) return null;

  return (
    <div
      role="alertdialog"
      aria-label="Lejárt az idő"
      className="absolute inset-0 z-40 grid place-items-center bg-black/45 p-4"
    >
      <div className="flex w-full max-w-sm animate-fade-rise flex-col items-center gap-3 rounded-card border-2 border-accent bg-surface-raised px-8 py-6 text-center shadow-(--shadow-modal)">
        <AlarmClock />
        <p className="font-display text-2xl font-semibold text-ink">Lejárt az idő!</p>
        <p className="text-sm text-ink-muted">
          {formatClock(timer.durationMs ?? 0, 'ceil')} letelt.
        </p>
        <button
          type="button"
          autoFocus
          onClick={() => dismissAlarm(timer)}
          className="rounded-control bg-accent px-5 py-2 text-sm font-semibold text-accent-ink hover:bg-accent-hover"
        >
          Rendben
        </button>
      </div>
    </div>
  );
}

/** A rezgo vekker: a teste razkodik, a mutatok pergnek. */
function AlarmClock() {
  return (
    <svg viewBox="0 0 120 120" className="timer-clock-ring size-36" aria-hidden="true">
      {/* A ket csengo es a labak. */}
      <circle cx="30" cy="30" r="12" fill="var(--accent)" />
      <circle cx="90" cy="30" r="12" fill="var(--accent)" />
      <path
        d="M36 100 L28 112 M84 100 L92 112"
        stroke="var(--accent)"
        strokeWidth="6"
        strokeLinecap="round"
      />
      {/* A szamlap. */}
      <circle cx="60" cy="66" r="40" fill="var(--surface)" stroke="var(--accent)" strokeWidth="6" />
      <g stroke="var(--ink-muted)" strokeWidth="3" strokeLinecap="round">
        <path d="M60 32 V38 M60 94 V100 M26 66 H32 M88 66 H94" />
      </g>
      <g className="timer-clock-hand-slow" style={{ transformOrigin: '60px 66px' }}>
        <path d="M60 66 V47" stroke="var(--ink)" strokeWidth="5" strokeLinecap="round" />
      </g>
      <g className="timer-clock-hand" style={{ transformOrigin: '60px 66px' }}>
        <path d="M60 66 V38" stroke="var(--danger)" strokeWidth="3.5" strokeLinecap="round" />
      </g>
      <circle cx="60" cy="66" r="4" fill="var(--ink)" />
    </svg>
  );
}

// ─── Nagy csempe a racsban ────────────────────────────────────────────────────

/**
 * A stopper/idozito NAGYBAN, a resztvevok racsaban - ugyanakkora blokk, mint egy
 * felhasznalo. A fejlec-gomb kicsi: az idot ket-harom meterrol is latni kell, es
 * a visszaszamlalas pont erre jo.
 *
 * Lejartakor pirosan villog; a "Rendben" utan (lasd `dismissAlarm`) eltunik.
 */
export function TimerTile({
  huddle,
  meId,
  compact = false,
}: {
  huddle: Huddle;
  meId: string;
  /** Kepmegosztas kozben a csempek egy keskeny savban allnak. */
  compact?: boolean;
}) {
  const dismissed = useAlarmDismissed(huddle.timer);
  const timer = dismissed ? null : huddle.timer;
  const nowMs = useServerClock(timer?.serverNow ?? null);
  useTick(timer !== null);
  if (!timer) return null;

  const canControl = canControlTimer(huddle.hostId, huddle.timerOpen, meId);
  const now = nowMs();
  const remaining = timerRemainingMs(timer, now);
  const finished = remaining !== null && remaining <= 0;
  const display =
    remaining !== null
      ? formatClock(remaining, 'ceil')
      : formatClock(timerElapsedMs(timer, now), 'floor');
  const label = timer.mode === 'countdown' ? 'Időzítő' : 'Stopper';
  const act = (input: Parameters<typeof huddleTimerAction>[1]) =>
    void huddleTimerAction(huddle.id, input).catch(() => undefined);

  const tone = finished
    ? 'border-[var(--danger)] bg-[var(--danger-soft)]'
    : timer.running
      ? 'border-accent bg-accent-soft'
      : 'border-border-soft bg-surface-sunken';

  return (
    <li
      title={timer.mode === 'countdown' ? 'Hátralévő idő' : 'Eltelt idő'}
      className={compact ? 'shrink-0' : 'flex flex-col gap-1.5'}
    >
      <div
        // A betumeret a csempe szelessegehez igazodik (container query).
        style={{ containerType: 'inline-size' }}
        className={[
          'relative grid place-items-center overflow-hidden rounded-card border-2',
          compact ? 'h-14 min-w-28 px-3' : 'aspect-[4/3] w-full',
          tone,
        ].join(' ')}
      >
        <time
          style={{ fontSize: compact ? '1.5rem' : 'clamp(1.75rem, 24cqw, 5rem)' }}
          className={[
            'font-display font-semibold leading-none tabular-nums text-ink',
            finished ? 'animate-pulse text-[var(--danger)]' : '',
          ].join(' ')}
        >
          <span className="sr-only">{label}: </span>
          {display}
        </time>

        {canControl && !compact && (
          <div className="absolute inset-x-1 bottom-1 flex items-center justify-center gap-1">
            <ChipButton
              label={timer.running ? 'Szünet' : 'Folytatás'}
              disabled={finished}
              onClick={() => act({ action: timer.running ? 'pause' : 'resume' })}
            >
              <Icon name={timer.running ? 'szunet' : 'folytatas'} />
            </ChipButton>
            <ChipButton label="Nullázás" onClick={() => act({ action: 'reset' })}>
              ⟲
            </ChipButton>
            <ChipButton label="Eltüntetés" onClick={() => act({ action: 'clear' })}>
              ×
            </ChipButton>
          </div>
        )}
      </div>
      {!compact && (
        <span className="flex items-center justify-center gap-1.5 text-xs font-medium text-ink-muted">
          <Icon name="ora" />
          {label}
        </span>
      )}
    </li>
  );
}
